import { Component } from '@angular/core';
import { DividerModule } from 'primeng/divider';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';
import { MarcaDTO, MarcaFormDTO } from '../../../models/marca.model';
import { MarcaService } from '../../../services/marca/marca.service';
import { environment } from '../../../environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { ImagemUtils } from '../../../shared/utils/imagem-utils';

@Component({
  selector: 'app-edit-marca',
  templateUrl: './edit-marca.component.html',
  styleUrl: './edit-marca.component.scss',
  providers: [ConfirmationService, MessageService],
})
export class EditMarcaComponent {
  form: MarcaFormDTO = this.createEmptyForm();

  // ---- ENV/empresa (usado para montar a URL da imagem do back)
  env = environment;
  idEmpresa: number | string = Number(localStorage.getItem('empresaId')) || 1;

  // ---- Upload / preview
  imageBase64: string | null = null;       // conteúdo que vai no PUT se o usuário trocar a imagem
  imagePreviewUrl: string | null = null;   // dataURL só para pré-visualização
  fileName: string | null = null;

  constructor(
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private marcaService: MarcaService,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService
  ) { }

  ngOnInit() {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.marcaService.getById('/marca/' + id).subscribe((response) => {
        this.form = {
          id: response.id,
          codigoMarca: response.codigoMarca ?? null,
          nome: response.nome,
          imagem: response.imagem, // nome do arquivo vindo do back
        };

        // se quiser já exibir a atual também como preview local, pode montar:
        // this.imagePreviewUrl = this.env.imgHost + '/' + this.idEmpresa + '/marca/' + (response.imagem || '');
      });
    }
  }

  // ===== Upload =====
  async onSelectImage(event: any) {
    const file: File | undefined = event.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      this.messageService.add({ severity: 'error', summary: this.translate.instant('marca-edit.texts.erro'), detail: this.translate.instant('marca-edit.texts.arquivo_invalido') });
      return;
    }

    this.fileName = file.name;

    const imagem = await ImagemUtils.comprimirParaBase64(file);
    this.imagePreviewUrl = imagem.dataUrl;                 // mostra preview local
    this.imageBase64 = imagem.base64; // guarda só o base64 para o PUT
  }

  onClearImage() {
    this.fileName = null;
    this.imageBase64 = null;
    this.imagePreviewUrl = null; // volta a mostrar a imagem do back (ngIf cai no template acima)
  }

  // ===================

  submit() {
    if (!this.form.nome) {
      this.messageService.add({ severity: 'error', summary: this.translate.instant('marca-edit.texts.erro'), detail: this.translate.instant('marca-edit.texts.nome_obrigatorio') });
      return;
    }
    if (this.form.codigoMarca === null) {
      this.messageService.add({ severity: 'error', summary: this.translate.instant('marca-edit.texts.erro'), detail: this.translate.instant('marca-edit.texts.pais_obrigatorio') });
      return;
    }

    // Envia a imagem somente se o usuário escolheu uma nova (base64)
    const body: MarcaDTO = {
      nome: this.form.nome!,
      codigoMarca: this.form.codigoMarca!,
      ...(this.imageBase64 ? { imagem: this.imageBase64 } : {}),
    };

    this.marcaService.put('/marca/' + this.form.id, body).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: this.translate.instant('marca-edit.texts.sucesso'), detail: this.translate.instant('marca-edit.texts.marca_editada_sucesso') });
        this.router.navigate(['/marca']);
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: this.translate.instant('marca-edit.texts.erro'), detail: this.translate.instant('marca-edit.texts.erro_ao_salvar_marca') });
      },
    });
  }

  onImageError(ev: Event) {
    (ev.target as HTMLImageElement).src = 'assets/images/no-image.png';
  }

  navigateBack() {
    this.router.navigate(['/marca']);
  }

  private createEmptyForm(): MarcaFormDTO {
    return { nome: '', codigoMarca: null };
  }
}
